import React, { useState } from 'react';
import axios from 'axios';
import { Check, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import BusinessInfoStep, { emptyBusinessInfo, validateBusinessInfo } from './BusinessInfoStep';
import StripeStep from './StripeStep';
import VehiclesStep, { emptyVehiclesData, validateVehicles } from './VehiclesStep';
import ScheduleStep from './ScheduleStep';
import { emptyAttendee, validateAttendee } from './AttendeeDetails';

const STEPS = ['Business Info', 'Payments', 'Vehicles', 'Schedule'];

const OnboardingForm = () => {
  const [step, setStep] = useState(0);
  const [business, setBusiness] = useState(emptyBusinessInfo);
  const [hasStripeAccount, setHasStripeAccount] = useState(false);
  const [vehicles, setVehicles] = useState(emptyVehiclesData);
  const [appointment, setAppointment] = useState(null); // { startTime, timezone }
  const [attendee, setAttendee] = useState(emptyAttendee);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Returns the validation errors for a given step ({} = valid).
  const validateStep = (s) => {
    if (s === 0) return validateBusinessInfo(business);
    if (s === 1) {
      return hasStripeAccount
        ? {}
        : { stripe: 'Please confirm you have an active Stripe account to continue.' };
    }
    if (s === 2) return validateVehicles(vehicles);
    return {};
  };

  const scrollToTop = () => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // After validation fails, scroll the first invalid field into view (centered)
  // and focus it, instead of jumping to the top of the page. Errors render on
  // the next paint, so wait a frame before querying the DOM.
  const scrollToFirstError = () => {
    if (typeof window === 'undefined') return;
    // Double rAF: ensures the DOM has painted even when we also changed step.
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const el = document.querySelector(
        '.preob-form [aria-invalid="true"], .preob-form .preob-field__error, '
        + '.preob-form .preob-phone--invalid, '
        + '.preob-form .preob-consent--invalid, .preob-form .preob-stripe-confirm--invalid, '
        + '.preob-form .preob-form__error, .preob-form .preob-step__error'
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function') {
          try { el.focus({ preventScroll: true }); } catch { /* noop */ }
        }
      } else {
        scrollToTop();
      }
    }));
  };

  const goNext = () => {
    const stepErrors = validateStep(step);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      scrollToFirstError();
      return;
    }
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    scrollToTop();
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    scrollToTop();
  };

  const handleSubmit = async () => {
    // Re-validate every gated step before the final submit.
    const attendeeErrors = validateAttendee(attendee);
    const allErrors = {
      ...validateBusinessInfo(business),
      ...validateVehicles(vehicles),
      ...attendeeErrors,
    };
    if (!hasStripeAccount) allErrors.stripe = 'Please confirm you have an active Stripe account.';
    if (!appointment || !appointment.startTime) allErrors.appointment = 'Please pick a date and time for your call.';
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      // Jump back to the earliest step that has an error.
      const businessErr = Object.keys(validateBusinessInfo(business)).length > 0;
      const stripeErr = !hasStripeAccount;
      const vehiclesErr = Object.keys(validateVehicles(vehicles)).length > 0;
      setStep(businessErr ? 0 : stripeErr ? 1 : vehiclesErr ? 2 : 3);
      scrollToFirstError();
      return;
    }
    setErrors({});
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...business,
        hasStripeAccount,
        vins: vehicles.vins.filter(Boolean),
        fileUrl: vehicles.fileUrl || null,
        googleDocUrl: vehicles.googleDocUrl || null,
        attendee,
        appointment,
      };
      const resp = await axios.post('/api/ghl-submit', payload);
      // The server returns ok:false with bookingError when the contact saved but
      // the appointment failed — surface that so the user can retry the time.
      if (resp.data && resp.data.ok === false && resp.data.bookingError) {
        setSubmitError(resp.data.bookingError);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      const msg =
        err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Something went wrong submitting your pre-boarding. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessScreen business={business} />;
  }

  return (
    <div className="preob-form">
      <div className="preob-form__head">
        <h1 className="preob-form__title">Begin pre-boarding</h1>
        <p className="preob-form__sub">
          Tell us about your business and fleet. Then schedule onboarding with a Rentid expert.
        </p>
      </div>

      <Stepper current={step} />

      {step === 1 ? (
        <div className="preob-form__step">
          <StripeStep />
        </div>
      ) : (
        <div className="preob-form__step preob-form__step--card">
          {step === 0 && <BusinessInfoStep value={business} onChange={setBusiness} errors={errors} />}
          {step === 2 && <VehiclesStep value={vehicles} onChange={setVehicles} errors={errors} />}
          {step === 3 && (
            <ScheduleStep
              appointment={appointment}
              onSelectSlot={setAppointment}
              attendee={attendee}
              onAttendeeChange={setAttendee}
              attendeeErrors={errors}
            />
          )}
        </div>
      )}

      {step === 1 && (
        <>
          <label className={`preob-stripe-confirm${errors.stripe ? ' preob-stripe-confirm--invalid' : ''}`}>
            <input
              type="checkbox"
              checked={hasStripeAccount}
              onChange={(e) => {
                setHasStripeAccount(e.target.checked);
                if (e.target.checked) setErrors((prev) => ({ ...prev, stripe: undefined }));
              }}
              className="preob-checkbox"
            />
            <span className="preob-stripe-confirm__text">
              I confirm I have an active Stripe account in my name.
            </span>
          </label>
          {errors.stripe && <p className="preob-field__error preob-consent__error">{errors.stripe}</p>}
        </>
      )}

      {step === 3 && errors.appointment && (
        <p className="preob-form__error">{errors.appointment}</p>
      )}
      {submitError && <p className="preob-form__error">{submitError}</p>}

      {/* Nav */}
      <div className="preob-form__nav">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 0 || submitting}
          className="preob-btn preob-btn--ghost"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {step < STEPS.length - 1 ? (
          <button type="button" onClick={goNext} className="preob-btn preob-btn--primary">
            Continue
            <ArrowRight size={16} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              submitting ||
              !appointment ||
              !appointment.startTime ||
              Object.keys(validateAttendee(attendee)).length > 0
            }
            className="preob-btn preob-btn--primary"
          >
            {submitting ? (
              <>
                Submitting…
                <Loader2 size={16} className="preob-spin" />
              </>
            ) : (
              <>
                Submit &amp; confirm meeting
                <Check size={16} />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const Stepper = ({ current }) => (
  <ol className="preob-stepper">
    {STEPS.map((label, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <li key={label} className="preob-stepper__item">
          <div className="preob-stepper__node">
            <span
              className={
                'preob-stepper__dot'
                + (done ? ' preob-stepper__dot--done' : active ? ' preob-stepper__dot--active' : '')
              }
            >
              {done ? <Check size={16} /> : i + 1}
            </span>
            <span
              className={`preob-stepper__label${active ? ' preob-stepper__label--active' : ''}`}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && <span className="preob-stepper__sep" />}
        </li>
      );
    })}
  </ol>
);

const SuccessScreen = ({ business }) => (
  <div className="preob-success">
    <span className="preob-success__icon">
      <Check size={32} strokeWidth={2.5} />
    </span>
    <h1 className="preob-success__title">Pre-boarding submitted</h1>
    <p className="preob-success__copy">
      Thanks{business.businessName ? `, ${business.businessName}` : ''}! A Rentid expert will reach out
      shortly to confirm your onboarding session and finish setting up your fleet.
    </p>
    <a href="/get-started" className="preob-btn preob-btn--primary preob-success__cta">
      Back to home
    </a>
  </div>
);

export default OnboardingForm;
